import {skipToken} from '@reduxjs/toolkit/query';
import {Action, Card, DataStatus, showNotification} from 'platform/components';
import {VStack} from 'platform/foundation';

import {useSelector} from 'react-redux';

import {isNil, isNotNil} from 'ramda';
import {isPositive} from 'ramda-adjunct';

import {VehicleAlbums} from '@dms/api/shared';
import {
  useCopyAuditPhotoToSaleVehicleAlbumMutation,
  useGetActiveSaleVehicleQuery,
  useListSaleVehicleAlbumsByVehicleQuery,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {
  AuditCategoryUniqueKey,
  selectAudit,
  selectCarAudit,
  selectStructure,
  selectUserSelectedLanguage,
} from '@dms/teas';

import {suffixTestId, TestIdProps, useRequiredParams} from 'shared/web';

import {getPhotosData} from '../utils/getPhotosData';
import {AuditPhotosCategory} from './AuditPhotosCategory';

interface AuditPhotosProps extends TestIdProps {
  notFoundText?: string;
}

export const AuditPhotos = (props: AuditPhotosProps) => {
  const {id: vehicleId} = useRequiredParams();
  const locale = useSelector(selectUserSelectedLanguage);
  const {loading} = useSelector(selectCarAudit);
  const auditStructure = useSelector(selectStructure);
  const auditData = useSelector(selectAudit);

  const {data: saleVehicle} = useGetActiveSaleVehicleQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken
  );

  const {data: albums} = useListSaleVehicleAlbumsByVehicleQuery(
    isNotNil(vehicleId) ? {vehicleId} : skipToken
  );

  const saleVehicleSalesAlbumId = albums?.find(
    (album) => album.name === VehicleAlbums.PHOTOS_CAR_SALES && album.active
  )?.id;

  const [copyAuditPhotoToSaleVehicleAlbum, {isLoading: isCopyingToSaleVehicleAlbum}] =
    useCopyAuditPhotoToSaleVehicleAlbumMutation();

  const categoriesAndPhotos = getPhotosData(
    locale,
    auditData ?? undefined,
    auditStructure ?? undefined
  ).sort((a, b) => {
    // Move PHOTODOCUMENTATION category on top of the list
    if (a.uniqueKey === AuditCategoryUniqueKey.PHOTODOCUMENTATION) {
      return -1;
    }
    if (b.uniqueKey === AuditCategoryUniqueKey.PHOTODOCUMENTATION) {
      return 1;
    }
    return 0;
  });

  const buildPhotoDocumentationActions = (categoryId: string): Action[] => [
    {
      type: 'button',
      variant: 'link',
      leftIcon: 'content/copy',
      title: i18n.t('entity.vehicle.actions.copyAuditPhoto'),
      onClick: () => {
        const saleVehicleId = saleVehicle?.id;
        const saleVehicleAlbumId = saleVehicleSalesAlbumId;

        if (isNil(auditData) || isNil(saleVehicleId) || isNil(saleVehicleAlbumId)) {
          showNotification.error();
          return;
        }

        copyAuditPhotoToSaleVehicleAlbum({
          saleVehicleId,
          saleVehicleAuditPhotoCopyRequestBody: {
            auditId: auditData.id,
            auditCategoryId: categoryId,
            saleVehicleAlbumId,
          },
        })
          .unwrap()
          .catch(handleApiError);
      },
      isLoading: isCopyingToSaleVehicleAlbum,
    },
  ];

  return (
    <DataStatus
      isLoading={loading.getCondition || loading.getAudit || loading.getAuditStructure}
      isEmpty={!isPositive(categoriesAndPhotos.length)}
      emptyMessage={props.notFoundText}
      minHeight={50}
    >
      <VStack spacing={3}>
        {categoriesAndPhotos?.map((category) => (
          <Card
            key={`${category.name}_${category.id}`}
            title={category.name}
            actions={
              category.uniqueKey === AuditCategoryUniqueKey.PHOTODOCUMENTATION
                ? buildPhotoDocumentationActions(category.id)
                : []
            }
            data-testid={suffixTestId(`[${category.name}]`, props)}
          >
            <VStack spacing={3}>
              {category.childCategories?.map((childCategory, index) => (
                <AuditPhotosCategory
                  key={`${category.name}_${childCategory.id}`}
                  category={childCategory}
                  data-testid={suffixTestId(`[${category.name}]-childCategories-[${index}]`, props)}
                />
              ))}
            </VStack>
          </Card>
        ))}
      </VStack>
    </DataStatus>
  );
};
